<template>
  <v-menu
    v-model="showSuggestions"
    left
    offset-y
    open-on-focus
    origin="right top"
    max-height="500"
    max-width="500"
    data-testid="explore-field-searchBar"
    data-tourid="explore-field-searchBar"
    :content-class="menuContentClass"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <div v-on="on">
        <v-text-field
          v-model="searchValue"
          clearable
          data-private
          dense
          hide-details
          outlined
          rounded
          class="search-input-field body-2"
          background-color="white"
          prepend-inner-icon="mdi-magnify"
          :loading="loadingContent"
          :placeholder="placeholder"
          @input="handleSearch"
          @keyup.enter="goToExploreView"
          @click:clear="handleClearInput"
        >
        </v-text-field>
      </div>
    </template>
    <SearchHistory
      v-if="shouldDisplaySearchHistory"
      :loading="loadingSearchHistory"
      :searchHistory="searchHistory"
      @select="handleSelectSearchHistory"
      @deleteAll="handleDeleteAllHistory"
      @deleteItem="handleDeleteItemHistory"
    />
    <SearchBarResults
      v-if="shouldDisplaySearchResults"
      :loading="loadingContent"
      :results="results"
      :isContentRequested="isContentRequested"
      :searchValue="searchValue"
      @viewMore="goToExploreView"
      @selectResult="goToEntity"
      @openFinding="openFindingDrawer"
      @openTask="openTask"
    />
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex'

import SearchHistory from '@/containers/Explore/SearchHistory.vue'
import SearchBarResults from '@/containers/Explore/SearchBarResults.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import api from '@/api'
import _ from 'lodash'

export default {
  name: 'ExploreSearchBar',

  mixins: [mixin, trackerMixin],

  components: {
    SearchHistory,
    SearchBarResults,
  },

  data() {
    return {
      searchValue: this.$route.query.search ?? '',
      showSuggestions: false,
      searchHistory: [],
      results: [],
      loadingSearchHistory: false,
      loadingContent: false,
      isContentRequested: false,
    }
  },

  mounted() {
    this.fetchSearchHistory()
  },

  computed: {
    ...mapGetters(['organizationId', 'account', 'organization']),

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    placeholder() {
      return this.hasRoutinesFeatureEnabled
        ? this.$t('explore.input_placeholder')
        : this.$t('explore.input_placeholder_without_routines')
    },
    shouldDisplaySearchResults() {
      return !_.isEmpty(this.results) || this.isContentRequested
    },
    shouldDisplaySearchHistory() {
      return !this.shouldDisplaySearchResults && !this.isSearchHistoryEmpty
    },
    isSearchHistoryEmpty() {
      return _.isEmpty(this.searchHistory)
    },
    menuContentClass() {
      return this.shouldDisplaySearchHistory || this.shouldDisplaySearchResults
        ? 'search-bar'
        : 'search-bar no-shadow'
    },
  },
  methods: {
    closeSuggestions() {
      this.showSuggestions = false
    },
    handleSearch: _.debounce(function(searchValue) {
      this.showSuggestions = true
      this.searchValue = searchValue ?? ''
      this.fetchContent(!!this.searchValue)
    }, 500),

    handleClearInput() {
      this.isContentRequested = false
      this.results = []
      this.$router.replace({
        query: { ...this.$route.query, search: undefined },
      })
      this.trackExploreSearch({
        action: 'clear',
        terms: this.searchValue,
        routeName: this.$route.name,
      })
    },
    async handleDeleteAllHistory() {
      try {
        await api.explore.deleteAllSearchHistory({
          organizationId: this.organizationId,
          ids: this.searchHistory.map(item => item.id),
        })
        this.showSuggestions = false
        this.searchHistory = []
        this.trackExploreHistory({
          action: 'deleteAll',
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },
    async handleDeleteItemHistory(itemId) {
      try {
        this.loadingSearchHistory = true
        const response = await api.explore.deleteSearchHistoryItem({
          historyId: itemId,
          organizationId: this.organizationId,
        })
        this.searchHistory = response?.data ?? []
        this.trackExploreHistory({
          action: 'delete',
          historyTerm: itemId,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingSearchHistory = false
      }
    },
    handleSelectSearchHistory(searchValue) {
      this.searchValue = searchValue
      this.goToExploreView(false)
      this.trackExploreHistory({
        action: 'click',
        historyTerm: searchValue,
      })
    },
    goToExploreView(allowSaveSearchHistory = true) {
      this.showSuggestions = false
      if (allowSaveSearchHistory && this.searchValue) this.saveSearchHistory()
      this.$router
        .push({
          path: '/explore',
          query: {
            ...this.$route.query,
            page: 1,
            search: this.searchValue,
          },
        })
        .catch(() => {})
      this.results = []
      this.isContentRequested = false
      this.trackExploreNavigate({
        option: 'viewMore',
        query: {
          ...this.$route.query,
          page: 1,
          search: this.searchValue,
        },
      })
    },
    async fetchContent() {
      try {
        if (!this.searchValue) return
        this.loadingContent = true
        const response = await api.explore.getContent({
          terms: this.searchValue,
          organizationId: this.organizationId,
          includeMatchingFields: true,
          sort: { by: '_score', order: 'desc' },
        })
        this.results = response?.data?.results ?? []
        this.isContentRequested = true
        this.trackExploreSearch({
          action: 'search',
          terms: this.searchValue,
          route: this.$route.path,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingContent = false
      }
    },
    async fetchSearchHistory() {
      try {
        this.loadingSearchHistory = true
        const response = await api.explore.getSearchHistory({
          organizationId: this.organizationId,
        })
        this.searchHistory = response?.data ?? []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingSearchHistory = false
      }
    },
    async saveSearchHistory() {
      try {
        const response = await api.explore.saveSearchHistory({
          organizationId: this.organizationId,
          term: this.searchValue,
        })
        this.searchHistory = response?.data ?? []
        this.trackExploreHistory({
          action: 'add',
          historyTerm: String(this.searchValue),
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },
    goToEntity(route) {
      this.saveSearchHistory()
      this.searchValue = ''
      this.results = []
      this.isContentRequested = false
      this.closeSuggestions()
      if (route) this.navigateTo(route)
      this.trackExploreNavigate({
        option: 'suggestion',
        route: this.$route.path,
      })
    },
    openFindingDrawer(findingId) {
      this.showSuggestions = false
      this.saveSearchHistory()
      this.$emit('openFinding', findingId)
    },
    openTask(taskId) {
      this.showSuggestions = false
      this.saveSearchHistory()
      this.$emit('openTask', taskId)
    },
  },
  watch: {
    '$route.query.search'(currentSearchParam) {
      this.searchValue = currentSearchParam ?? ''
      if (this.$route.name !== 'explore') {
        this.results = []
        this.isContentRequested = false
      }
    },
  },
}
</script>

<style lang="scss">
.search-bar.no-shadow {
  box-shadow: none;
}
@media (max-width: 550px) {
  .search-bar {
    width: 100vw;
    left: 0 !important;
    right: 0;
    top: 50px !important;
  }
}
</style>
